import { Layers, LogOut } from 'lucide-react'
import { useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Toaster } from '@/components/ui/Toaster'
import { useAuth } from '@/features/auth/store'
import { cn } from '@/lib/cn'

/**
 * Layout for `/m/*` (ADR-007) — a field rep's phone, not a desk. Three differences from
 * `AppShell` and none of them are cosmetic:
 *
 * - The top and bottom bars pad for the safe area (`env(safe-area-inset-*)`) so content
 *   never sits under a notch or a home indicator. `viewport-fit=cover` in `index.html` is
 *   what makes those env() values non-zero at all.
 * - Every tap target here is at least 44px (`h-11`), the platform-recommended minimum —
 *   `AppShell`'s `sm`/`md` buttons are sized for a mouse, not a thumb.
 * - Navigation is a fixed bottom bar, not a sidebar: there is nowhere to put a sidebar on
 *   a phone, and a bottom bar is within thumb reach the way a top one is not.
 */
export function MobileShell() {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <TopBar />
      <main className="flex-1 overflow-y-auto px-4 py-4">
        <Outlet />
      </main>
      <BottomNav />
      <Toaster />
    </div>
  )
}

function TopBar() {
  const user = useAuth((s) => s.user)
  const signOut = useAuth((s) => s.signOut)
  const navigate = useNavigate()
  const [signingOut, setSigningOut] = useState(false)

  const handleSignOut = async () => {
    setSigningOut(true)
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <header
      className="flex items-center justify-between border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
    >
      <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-cyan-500 shadow-md shadow-emerald-500/20">
          <Layers className="h-4.5 w-4.5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
            FMCG <span className="text-emerald-600 dark:text-emerald-400">ERP</span>
          </p>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
            {user?.full_name ?? 'Field rep'}
          </p>
        </div>
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 shrink-0"
        onClick={handleSignOut}
        loading={signingOut}
        aria-label="Sign out"
      >
        <LogOut className="h-5 w-5" aria-hidden="true" />
      </Button>
    </header>
  )
}

const TABS = [
  { to: '/m', label: 'Today', end: true },
  { to: '/m/route', label: 'Route', end: false },
]

function BottomNav() {
  return (
    <nav
      className="flex border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              'flex h-14 flex-1 items-center justify-center text-sm font-medium',
              isActive
                ? 'text-brand-600 dark:text-brand-400'
                : 'text-slate-500 dark:text-slate-400',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  )
}
